'use client'

import type { ForwardRefRenderFunction } from 'react'
import React, {
  type ElementType,
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactElement,
} from 'react'

import {
  type FontSize,
  getTextBaseClasses,
  type TextOptions,
} from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'

import type {
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
} from '../../types/props.ts'
import { getTestProps, type TestIds } from '../../utils/testing.ts'

type TextBaseProps = PropsWithChildren<
  Pick<HTMLAttributes<HTMLElement>, 'className' | 'onClick'> &
    TestIds &
    Omit<TextOptions, 'fontSize'> & {
      fontSize?: FontSize
    }
>

type TextBasePropsWithRef<T extends ElementType> =
  PolymorphicComponentPropsWithRef<T, TextBaseProps>

export type TextBaseComponent = <T extends ElementType = 'span'>(
  props: TextBasePropsWithRef<T>
) => ReactElement | null

const component = <T extends ElementType = 'span'>(
  {
    as,
    children,
    dataTestId,
    dataTrackingId,
    className,
    onClick,
    ...textOptions
  }: TextBasePropsWithRef<T>,
  ref: PolymorphicRef<T>
) => {
  const Component = as || 'span'

  return (
    <Component
      ref={ref}
      className={cn(
        'whitespace-pre-wrap wrap-break-word',
        getTextBaseClasses({
          ...textOptions,
          textColor: textOptions.textColor ?? 'primary',
          fontSize: textOptions.fontSize ?? 'sm',
          fontWeight: textOptions.fontWeight ?? 'normal',
        }),
        'leading-normal',
        onClick ? 'cursor-pointer' : '',
        className
      )}
      {...getTestProps({ dataTestId, dataTrackingId })}
      onClick={onClick}
    >
      {children}
    </Component>
  )
}

type RequiredType = ForwardRefRenderFunction<
  unknown,
  Omit<TextBasePropsWithRef<ElementType>, 'ref'>
>

const TextBase = React.forwardRef(
  component as RequiredType
) as TextBaseComponent

export { TextBase, type TextBaseProps }
